import { HardDrive, AlertTriangle } from "lucide-react";

interface StorageUsageBarProps {
  label: string;
  used: number;
  total: number;
  unit?: string;
  showWarning?: boolean;
}

export default function StorageUsageBar({
  label,
  used,
  total,
  unit = "MB",
  showWarning = false,
}: StorageUsageBarProps) {
  const percentage = Math.min((used / total) * 100, 100);
  const isNearLimit = percentage >= 80;
  const isFull = percentage >= 100;

  const formatSize = (size: number): string => {
    if (size >= 1024) {
      return `${(size / 1024).toFixed(1)} GB`;
    }
    return `${size.toFixed(0)} ${unit}`;
  };

  const getBarColor = () => {
    if (isFull) return "bg-red-500";
    if (isNearLimit) return "bg-yellow-500";
    return "bg-gradient-to-r from-blue-500 to-pink-500";
  };

  const getBackgroundColor = () => {
    if (isFull) return "bg-red-100";
    if (isNearLimit) return "bg-yellow-100";
    return "bg-gray-200";
  };

  return (
    <div className="bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-blue-300 transition-all">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-gray-600" />
          <span className="font-semibold text-textDark">{label}</span>
        </div>
        {(isNearLimit || showWarning) && (
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
        )}
      </div>

      <div className="mb-2">
        <div className={`w-full h-3 ${getBackgroundColor()} rounded-full overflow-hidden`}>
          <div
            className={`h-full ${getBarColor()} transition-all duration-500 ease-out`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-textLight">
          {formatSize(used)} / {formatSize(total)}
        </span>
        <span className={`font-bold ${isFull ? "text-red-600" : isNearLimit ? "text-yellow-600" : "text-gray-600"}`}>
          {percentage.toFixed(0)}%
        </span>
      </div>

      {isFull && (
        <p className="text-xs text-red-600 mt-2">
          พื้นที่เต็ม กรุณาอัปเกรดแพ็กเกจ
        </p>
      )}
      {isNearLimit && !isFull && (
        <p className="text-xs text-yellow-600 mt-2">
          ใกล้ถึงขีดจำกัด แนะนำให้อัปเกรด
        </p>
      )}
    </div>
  );
}
